import { INITIAL_FORM_DATA } from "../models/constants";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import type { TechFormSubmissionDraft } from "../models/tech-form-submission-data.models";
import type { TechFormVesselCacheIdentity } from "../models/tech-form-vessel-cache.models";
import { mapSqlToTq1VesselDetailsForm } from "../models/tq1-vessel-details.models";
import { mapSqlToTq2DesignDetailsForm } from "../models/tq2-design-details.models";
import { mapSqlToTq3DraftForm, mapSqlToTq3DraftSummerWinterForm } from "../models/tq3-draft.models";
import { mapSqlToTq4AntifoulingForm } from "../models/tq4-antifouling.models";
import { mapSqlToTq5PortCallsForm } from "../models/tq5-port-calls.models";
import { mapSqlToTq6SecaForm, tq6SecaFormInit } from "../models/tq6-seca.models";
import { mapSqlToTq7SlowSteamingForm, tq7SlowSteamingFormInit } from "../models/tq7-slow-steaming.models";
import {
  mapSqlToTq8PowerManagementForm,
  mapSqlToTq8PowerManagementTable,
  tq8PowerManagementFormInit,
} from "../models/tq8-power-management.models";
import { prepareDraftRowsForEditing } from "../utils/prepare-draft-rows-for-editing.utils";
import { loadTechFormEndpointData } from "./tech-form-section-data.service";

export async function loadTechFormSubmissionDraft(
  request: TechFormVesselCacheIdentity,
): Promise<TechFormSubmissionDraft> {
  const loadEndpoint = <T>(endpoint: (typeof TECH_FORM_ENDPOINTS)[keyof typeof TECH_FORM_ENDPOINTS]) =>
    loadTechFormEndpointData<T>({ ...request, endpoint });

  const [
    generalInformation,
    contacts,
    vesselDesign,
    vesselDraftSummerWinter,
    vesselDraft,
    antifoulingDetails,
    lastPortCalls,
    seca,
    slowSteaming,
    powerManagementIndividualFields,
    powerManagement,
  ] = await Promise.all([
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.generalInformation),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.contacts),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.vesselDesign),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.vesselDraftSummerWinter),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.vesselDraft),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.antifoulingDetails),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.lastPortCalls),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.seca),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.slowSteaming),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.powerManagementIndividualFields),
    loadEndpoint<any[]>(TECH_FORM_ENDPOINTS.powerManagement),
  ]);

  const mappedDraftRows = mapSqlToTq3DraftForm(vesselDraft).sort(
    (firstRow: { id: number }, secondRow: { id: number }) => firstRow.id - secondRow.id,
  );

  return {
    tq1VesselDetails: mapSqlToTq1VesselDetailsForm(
      generalInformation[0],
      contacts,
      request.vesselName,
      request.imoNumber,
    ),
    tq2DesignDetails: mapSqlToTq2DesignDetailsForm(vesselDesign[0]),
    tq3Draft: mapSqlToTq3DraftSummerWinterForm(vesselDraftSummerWinter[0]),
    tq3DraftTable: prepareDraftRowsForEditing(mappedDraftRows),
    tq4AntiFoulingDetails: antifoulingDetails[0]
      ? mapSqlToTq4AntifoulingForm(antifoulingDetails[0])
      : { ...INITIAL_FORM_DATA },
    tq5PortCalls: { rows: mapSqlToTq5PortCallsForm(lastPortCalls) },
    tq6Seca: seca[0] ? mapSqlToTq6SecaForm(seca[0]) : { ...tq6SecaFormInit },
    tq7SlowStreaming: slowSteaming[0] ? mapSqlToTq7SlowSteamingForm(slowSteaming[0]) : { ...tq7SlowSteamingFormInit },
    tq8PowerManagement: powerManagementIndividualFields[0]
      ? mapSqlToTq8PowerManagementForm(powerManagementIndividualFields[0])
      : { ...tq8PowerManagementFormInit },
    tq8PowerManagementTable: mapSqlToTq8PowerManagementTable(powerManagement),
  };
}
